import { useFormikContext } from "formik";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import { apiConfiguration } from "../../api-configuration";
import { logError } from "../../errors";
import { AssetApi, AssetDto } from "../../lib-api";
import { AssetCard } from "./asset-card";
import { RenderError } from "./errors-list";
import { AssetMeta } from "../../lib-api/models/AssetMeta";

const api = new AssetApi(apiConfiguration);

interface AssetUploadProps {
  csrf: string;
  helperText?: string;
  maxAssets: number;
  rounded?: boolean;
}

export const AssetUpload: React.FC<AssetUploadProps> = <
  T extends { assets: AssetDto[] }
>({
  csrf,
  helperText,
  maxAssets,
  rounded,
}) => {
  const [uploadCounter, setUploadCounter] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { isSubmitting, values, setValues, errors, touched } =
    useFormikContext<T>();

  const [assets, setAssets] = useState<AssetDto[]>(values.assets);

  const handleFileUpload = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(event.target.files ?? []);
      if (files.length + assets.length <= maxAssets) {
        files.forEach((file) => {
          (async () => {
            setUploadCounter((previous) => previous + 1);
            try {
              const asset = await api.putAsset(
                {
                  formFile: file,
                },
                { headers: { RequestVerificationToken: csrf } }
              );
              setAssets((previous) => previous.concat([asset]));
            } catch (e) {
              if (!(e instanceof Error)) {
                e = new Error(e);
              }
              logError(e);
            }
            setUploadCounter((previous) => previous - 1);
          })();
        });
      } else {
        // Handle case where the selected files exceed the max limit
        alert(`You can only upload up to ${maxAssets} assets.`);
      }

      event.target.value = "";
    },
    [setAssets, setUploadCounter, assets.length, maxAssets, csrf]
  );

  const removeAsset = useCallback(
    (asset: AssetDto) => {
      setAssets(assets.filter((x) => x.id != asset.id));
    },
    [assets]
  );

  useEffect(() => {
    setValues((previous) => ({
      ...previous,
      assets: assets,
    }));
  }, [assets, setValues]);

  const onAssetUpdated = useCallback(
    (meta: AssetMeta) => {
      setAssets((previous) => {
        const asset = previous.find((x) => x.id == meta.id);
        if (asset) {
          asset.caption = meta.caption;
        }
        return [...previous];
      });
    },
    [setAssets]
  );

  return (
    <Form.Group>
      <Form.Label>
        {maxAssets > 1 ? "Billeder" : "Billede"}{" "}
        {uploadCounter > 0 ? "(Uploader...)" : ""}
      </Form.Label>
      <Form.Text className="font-weight-bold mb-2">
        {helperText ??
          "Billederne må gerne være af dit team, dig selv, virksomheden, eller noget i den dur. Derudover skal de gerne være på vandret led (bredformat) og i så høj opløsning som muligt."}
      </Form.Text>
      <Row>
        {assets.map((asset) => (
          <Col key={asset.id} md={2} lg={3} xs={6} sm={4} className="px-2 pb-2">
            <AssetCard
              api={api}
              asset={asset}
              disabled={isSubmitting}
              removeAsset={removeAsset}
              assetUpdated={onAssetUpdated}
              rounded={rounded ?? false}
            />
          </Col>
        ))}
      </Row>
      {assets.length < maxAssets && (
        <Button
          variant="outline-secondary"
          type="button"
          disabled={isSubmitting || uploadCounter > 0}
          onClick={() => fileInputRef.current?.click()}
        >
          {maxAssets > 1 ? "Vælg billeder" : "Vælg billede"}
        </Button>
      )}
      <Form.Control
        ref={fileInputRef}
        isInvalid={errors.assets != null}
        name="assets"
        type="file"
        accept="image/*"
        multiple={maxAssets > 1}
        className="d-none"
        disabled={
          isSubmitting || uploadCounter > 0 || assets.length >= maxAssets
        }
        onChange={handleFileUpload}
      />
      {touched.assets && errors.assets && <RenderError error={errors.assets} />}
    </Form.Group>
  );
};
